import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ScenarioMenu, { MENU_WIDTH } from './ScenarioMenu'
import AddScenarioMenu, { ADD_MENU_WIDTH } from './AddScenarioMenu'
import DeleteScenarioModal from './DeleteScenarioModal'

// A chip cell is its label plus px-[12px] each side; the selected pill adds px-[16px] each side.
const CHIP_CELL_PADDING = 24
const SELECTED_PILL_PADDING = 32

// Keeps the menu on-screen once a chip's scroll position pushes it past either edge -- no vertical
// clamp, on purpose. See README.md ("the scenario menu's horizontal clamp").
const MENU_EDGE_MARGIN = 8
const clampMenuLeft = (left, width = MENU_WIDTH) => Math.max(MENU_EDGE_MARGIN, Math.min(left, window.innerWidth - width - MENU_EDGE_MARGIN))

// The backend caps a scenario title at 120 characters (FLIP_STRING_MAX in flipAnalysisFields.js).
export const SCENARIO_TITLE_MAX = 120

// Fixed 105x32, rx 4 -- the only chip width in spec 3.3 that is NOT content-driven (contrast the
// selected/unselected cells below, sized purely by nested padding). Text inset 16 from the box
// left.
function RenameInput({ value, onChange, onCommit, onCancel }) {
  return (
    <div className="h-[32px] flex items-center px-[12px] shrink-0">
      <input
        autoFocus
        maxLength={SCENARIO_TITLE_MAX}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCommit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onCommit()
          if (e.key === 'Escape') onCancel()
        }}
        className="h-[32px] w-[105px] rounded-[4px] bg-white pl-[16px] text-[13px] text-[#1A2A10] outline-none"
        style={{ border: '1px solid #4CBC18' }}
      />
    </div>
  )
}

// Unselected: `12 + W + 12`, plain text. Selected: `12 + (16 + W + 16) + 12`, an `#EEF3EC` pill
// nested inside the same cell padding -- see README.md ("chip width is CSS composition...").
// Hover (letter 99, 5.2/5.3): an unselected chip fills its own cell #EEF3EC, so the width never
// moves; the selected pill goes #DDE9D8 like every other #EEF3EC control.
function ScenarioChip({ scenario, selected, onSelect, onContextMenu }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      onContextMenu={onContextMenu}
      className={`group h-[32px] flex items-center px-[12px] shrink-0 whitespace-nowrap rounded-[16px] ${selected ? '' : 'hover:bg-[#EEF3EC]'}`}
    >
      {selected ? (
        <span className="h-[32px] flex items-center px-[16px] rounded-[16px] bg-[#EEF3EC] group-hover:bg-[#DDE9D8]">
          <span data-chip-label="selected" className="text-[13px] font-extrabold text-[#4AA52A]">{scenario.title}</span>
        </span>
      ) : (
        <span data-chip-label="unselected" className="text-[13px] font-semibold text-brand-dark">{scenario.title}</span>
      )}
    </button>
  )
}

function PlusIcon({ color }) {
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
      <path d="M4 0.5V7.5" stroke={color} strokeLinecap="round" />
      <path d="M0.5 4H7.5" stroke={color} strokeLinecap="round" />
    </svg>
  )
}

// 126x32, rx 16 (spec 3.4). Hover recolours border/icon/label to #328C12 -- driven by React state
// like FlipInfoIcon's own hover, not a CSS-only pseudo-class, since the icon's SVG stroke has to
// move with it. Inset outline, not a Tailwind `border` -- see README.md ("Add Scenario's outline,
// not border, and the label wrap it fixes"). While its drop-down is open it keeps the hover colours
// (01.10 export #scenario_7 draws the open button that way).
function AddScenarioButton({ onClick, open, buttonRef }) {
  const [hovered, setHovered] = useState(false)
  const lit = hovered || open
  const color = lit ? '#328C12' : '#4AA52A'
  const outlineColor = lit ? '#328C12' : '#C6CFC6'
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="inline-flex items-center gap-x-[6px] w-[126px] h-[32px] rounded-[16px] bg-white px-[16px] shrink-0 whitespace-nowrap"
      style={{ outline: `1px solid ${outlineColor}`, outlineOffset: '-1px' }}
    >
      <PlusIcon color={color} />
      <span className={`text-[13px] font-semibold ${lit ? 'text-[#328C12]' : 'text-brand-dark'}`}>Add Scenario</span>
    </button>
  )
}

const NO_TEMPLATE_OPTIONS = { templates: null, loadFailed: false, load: () => {} }
const NO_ADDED_IDS = new Set()

// Controlled: pages/Flip.jsx owns the scenarios, the active id and every write (README.md,
// "Scenarios"). The ribbon keeps only its own UI state -- the open menus, the rename draft, the track cap.
// Callbacks take a scenario id; `onAdd` takes the template id picked in Add Scenario's drop-down.
export default function ScenarioRibbon({
  scenarios,
  activeId,
  onSelect,
  onAdd,
  onRename,
  onDuplicate,
  onDelete,
  templateOptions = NO_TEMPLATE_OPTIONS,
  addedTemplateIds = NO_ADDED_IDS,
}) {
  const selectedId = activeId
  const [editingId, setEditingId] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [menu, setMenu] = useState(null) // { scenarioId, left, top }
  // Delete asks first (README.md, "Delete asks first"); only the confirm calls onDelete.
  const [deleteId, setDeleteId] = useState(null)
  // Canvas: "After 3 scenarios, the Scenario component can be horizontally scrolled". From a fourth
  // chip the track is capped at the first three chips' unselected widths plus one selected pill, so
  // moving the selection never resizes it. README.md ("Page layout") has the arithmetic.
  const trackRef = useRef(null)
  const [trackMaxWidth, setTrackMaxWidth] = useState(null)
  // Unselected label widths by id + title: the selected label is bolder, hence a few px wider.
  const labelWidthsRef = useRef(new Map())
  useLayoutEffect(() => {
    const chips = trackRef.current?.children
    if (!chips) return
    // Every unselected chip is cached on every run, so a chip that later moves into the first three
    // while selected (a delete) is still counted at its unselected width.
    const labels = [...chips].map((chip) => chip.querySelector('[data-chip-label]'))
    const keyOf = (i) => `${scenarios[i].id}:${scenarios[i].title}`
    labels.forEach((label, i) => {
      if (label?.dataset.chipLabel === 'unselected' && label.offsetWidth > 0) labelWidthsRef.current.set(keyOf(i), label.offsetWidth)
    })
    if (chips.length <= 3) {
      setTrackMaxWidth(null)
      return
    }
    // A chip being renamed has no label to measure; keep the current cap until the rename ends.
    if (labels.slice(0, 3).some((label) => !label)) return
    // A title never seen unselected (renamed while selected) falls back to its selected width once;
    // the run after it is deselected measures it properly.
    const widths = labels.slice(0, 3).map((label, i) => labelWidthsRef.current.get(keyOf(i)) ?? label.offsetWidth)
    const width = widths.reduce((sum, w) => sum + w + CHIP_CELL_PADDING, SELECTED_PILL_PADDING)
    setTrackMaxWidth(widths.every((w) => w > 0) ? width : null)
  }, [scenarios, selectedId, editingId])
  // Keeps the selected chip in view once the track scrolls; a chip wider than the track shows its start.
  // Re-run when the track itself resizes (a window resize), not only on a list or selection change.
  const scrollSelectedIntoView = () => {
    const track = trackRef.current
    const chip = track?.children[scenarios.findIndex((s) => s.id === selectedId)]
    if (!chip) return
    const start = chip.offsetLeft - track.offsetLeft
    const end = start + chip.offsetWidth
    if (start < track.scrollLeft) track.scrollLeft = start
    else if (end > track.scrollLeft + track.clientWidth) track.scrollLeft = Math.min(start, end - track.clientWidth)
  }
  const scrollSelectedRef = useRef(scrollSelectedIntoView)
  scrollSelectedRef.current = scrollSelectedIntoView
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(scrollSelectedIntoView, [scenarios, selectedId, trackMaxWidth])
  useEffect(() => {
    const track = trackRef.current
    if (!track || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(() => scrollSelectedRef.current())
    observer.observe(track)
    return () => observer.disconnect()
  }, [])

  const closeMenu = () => setMenu(null)

  // Add Scenario's drop-down: { left, top } in viewport coordinates, or null. Its left edge is the
  // button's, 1 below it (01.10 export: panel y = button bottom + 1).
  const addButtonRef = useRef(null)
  const [addMenu, setAddMenu] = useState(null)
  const { load: loadTemplates } = templateOptions
  const toggleAddMenu = () => {
    if (addMenu) return setAddMenu(null)
    const rect = addButtonRef.current.getBoundingClientRect()
    setAddMenu({ left: clampMenuLeft(rect.left, ADD_MENU_WIDTH), top: rect.bottom + 1 })
    loadTemplates()
  }
  // Focus inside the closing panel goes back to the button, never to the page top.
  const closeAddMenu = useCallback((options) => {
    const focusInside = document.getElementById('portal')?.querySelector('[role="menu"]')?.contains(document.activeElement)
    setAddMenu(null)
    if (options?.refocus || focusInside) addButtonRef.current?.focus({ preventScroll: true })
  }, [])
  const pickTemplate = (templateId) => {
    closeAddMenu({ refocus: true })
    onAdd(templateId)
  }

  const handleContextMenu = (e, scenario) => {
    e.preventDefault()
    const chipRect = e.currentTarget.getBoundingClientRect()
    setMenu({
      scenarioId: scenario.id,
      // Viewport coordinates, not container-relative: ScenarioMenu is portaled and position: fixed
      // (ScenarioMenu.jsx), so it needs the same coordinate space getBoundingClientRect returns.
      // +12 -- the cell's own left padding, where both the selected pill and the unselected label
      // start (spec 3.2), so this anchor is correct for a right-click on either chip state.
      left: clampMenuLeft(chipRect.left + 12),
      top: chipRect.bottom,
    })
  }

  const startEdit = (scenario) => {
    setEditingId(scenario.id)
    setEditValue(scenario.title)
    closeMenu()
  }

  // A blank or unchanged title is not a write.
  const commitEdit = () => {
    const value = editValue.trim()
    const current = scenarios.find((s) => s.id === editingId)
    setEditingId(null)
    if (current && value && value !== current.title) onRename(editingId, value)
  }

  const cancelEdit = () => setEditingId(null)

  const runAction = (action, scenario) => {
    closeMenu()
    action(scenario.id)
  }

  const menuScenario = menu ? scenarios.find((s) => s.id === menu.scenarioId) : null
  const deleteScenario = deleteId ? scenarios.find((s) => s.id === deleteId) : null

  const askDelete = (scenario) => {
    closeMenu()
    setDeleteId(scenario.id)
  }
  const cancelDelete = useCallback(() => setDeleteId(null), [])
  const confirmDelete = () => {
    setDeleteId(null)
    onDelete(deleteScenario.id)
  }

  return (
    <div className="flex justify-end">
      <div
        data-testid="scenario-ribbon"
        className="relative flex items-center min-w-0 max-w-full h-[50px] rounded-[8px] bg-white pl-[12px] pr-[12px] py-[8px]"
        style={{ outline: '1px solid #C6CFC6', outlineOffset: '-1px' }}
      >
        {/* ScenarioMenu portals to #portal (ScenarioMenu.jsx), so it is no longer at risk of the
            overflow-x/overflow-y CSS coupling this comment used to guard against -- this
            overflow-x-auto is only here to let the chip track scroll when scenarios overflow the
            ribbon width. See README.md ("the scenario menu is portaled"). */}
        <div
          ref={trackRef}
          className="flex items-stretch min-w-0 overflow-x-auto"
          style={trackMaxWidth ? { maxWidth: trackMaxWidth } : undefined}
        >
          {scenarios.map((scenario) =>
            scenario.id === editingId ? (
              <RenameInput key={scenario.id} value={editValue} onChange={setEditValue} onCommit={commitEdit} onCancel={cancelEdit} />
            ) : (
              <ScenarioChip
                key={scenario.id}
                scenario={scenario}
                selected={scenario.id === selectedId}
                onSelect={() => onSelect(scenario.id)}
                onContextMenu={(e) => handleContextMenu(e, scenario)}
              />
            )
          )}
        </div>
        <div className="ml-[12px] shrink-0">
          <AddScenarioButton onClick={toggleAddMenu} open={Boolean(addMenu)} buttonRef={addButtonRef} />
        </div>
        {addMenu ? (
          <AddScenarioMenu
            style={{ left: addMenu.left, top: addMenu.top }}
            templates={templateOptions.templates}
            loadFailed={templateOptions.loadFailed}
            onRetry={loadTemplates}
            addedIds={addedTemplateIds}
            onPick={pickTemplate}
            onClose={closeAddMenu}
            anchorRef={addButtonRef}
          />
        ) : null}
        {menuScenario ? (
          <ScenarioMenu
            style={{ left: menu.left, top: menu.top }}
            onEdit={() => startEdit(menuScenario)}
            onDuplicate={() => runAction(onDuplicate, menuScenario)}
            onDelete={() => askDelete(menuScenario)}
            onClose={closeMenu}
          />
        ) : null}
        <DeleteScenarioModal open={Boolean(deleteScenario)} onConfirm={confirmDelete} onClose={cancelDelete} />
      </div>
    </div>
  )
}
